import { create } from 'zustand'
import { User } from '@shared/types'
import type { PointApiResponse } from '../services/pointActivityService'
import {
    maskPointIdentifier,
    pointDebug,
} from '../utils/pointDebug'

// Keys for localStorage persistence (mock until real API)
const ACCOUNTS_STORAGE_KEY = 'adam_tiktok_accounts'
const LAST_ACTIVE_ACCOUNT_KEY = 'adam_last_active_account'


export interface TikTokAccount {
    id: string;
    username: string;
    displayName?: string;
    avatarUrl: string;
    isActive: boolean;
    role?: 'owner' | 'manager' | 'viewer';
    coinsTotal: number;
    pointsTotal: number;
    pointsResetAt?: string | null;
    pointsCycleStartedAt?: string | null;
    stats?: {
        followers: number;
        likes: number;
        views: number;
        videos: number;
    };
}

interface AuthState {
    user: User | null
    accounts: TikTokAccount[]
    accountsLoaded: boolean
    pointStatusByAccount: Record<string, PointApiResponse>
    pointErrorByAccount: Record<string, string | null>
    pointLiveActiveSecondsByAccount: Record<string, number>
    isLoading: boolean
    error: string | null
    setUser: (user: User | null) => void
    setAccounts: (accounts: TikTokAccount[]) => void
    loadAccounts: () => void
    setActiveAccount: (id: string) => void
    updateAccountWallet: (
        id: string,
        patch: {
            pointsTotal?: number
            coinsTotal?: number
            pointsResetAt?: string | null
            pointsCycleStartedAt?: string | null
        }
    ) => void
    setPointStatus: (id: string, status: PointApiResponse) => void
    setPointLiveActiveSeconds: (id: string, activeSeconds: number) => void
    setPointError: (id: string, error: string | null) => void
    setLoading: (loading: boolean) => void
    setError: (error: string | null) => void
    logout: () => void
}


/**
 * Global authentication state using Zustand for reactive updates.
 * Manages user session and TikTok accounts across all dashboard components.
 * Accounts are persisted to localStorage until real TikTok OAuth API is implemented.
 */

export const useAuthStore = create<AuthState>((set) => ({
    user: null,
    accounts: [],
    accountsLoaded: false,
    pointStatusByAccount: {},
    pointErrorByAccount: {},
    pointLiveActiveSecondsByAccount: {},
    isLoading: true,
    error: null,
    setUser: (user) => set({ user, isLoading: false }),
    
    setAccounts: (accounts) => {
        // Persist to localStorage (mock persistence until real API)
        pointDebug('accounts.server_applied', {
            count: accounts.length,
            accounts: accounts.map((account) => ({
                id: maskPointIdentifier(account.id),
                role: account.role ?? null,
                isActiveFromServer: account.isActive,
                pointsTotal: account.pointsTotal,
                coinsTotal: account.coinsTotal,
            })),
        })
        localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accounts))
        
        // If there's a last active account, restore it
        const lastActiveId = localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY)
        const updatedAccounts = accounts.map(acc => ({
            ...acc,
            isActive: acc.id === lastActiveId || (lastActiveId === null && accounts.indexOf(acc) === 0)
        }))
        
        set({ accounts: updatedAccounts, accountsLoaded: true })
    },
    
    loadAccounts: () => {
        // Load from localStorage on app startup
        const stored = localStorage.getItem(ACCOUNTS_STORAGE_KEY)
        if (stored) {
            try {
                const accounts = JSON.parse(stored) as TikTokAccount[]
                pointDebug('accounts.cache_loaded', {
                    count: accounts.length,
                    accounts: accounts.map((account) => ({
                        id: maskPointIdentifier(account.id),
                        role: account.role ?? null,
                        isActiveCached: account.isActive,
                        pointsTotal: account.pointsTotal,
                        coinsTotal: account.coinsTotal,
                    })),
                })
                const lastActiveId = localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY)
                const updatedAccounts = accounts.map(acc => ({
                    ...acc,
                    isActive: acc.id === lastActiveId || (lastActiveId === null && accounts.indexOf(acc) === 0)
                }))
                set({ accounts: updatedAccounts, accountsLoaded: true })
            } catch {
                pointDebug('accounts.cache_invalid')
                set({ accounts: [], accountsLoaded: true })
            }
        } else {
            pointDebug('accounts.cache_empty')
            set({ accountsLoaded: true })
        }
    },
    
    setActiveAccount: (id) => {
        // Persist last active account to localStorage
        // This allows cookie-based persistence across tabs
        localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, id)
        
        set((state) => ({
            accounts: state.accounts.map(acc => ({
                ...acc,
                isActive: acc.id === id
            }))
        }))
    },

    updateAccountWallet: (id, patch) => {
        pointDebug('wallet.store_patch', {
            accountId: maskPointIdentifier(id),
            patch,
        })
        set((state) => {
            let changed = false
            const nextAccounts = state.accounts.map((account) => {
                if (account.id !== id) return account

                const next = {
                    ...account,
                    ...patch,
                }

                const same =
                    next.pointsTotal === account.pointsTotal &&
                    next.coinsTotal === account.coinsTotal &&
                    next.pointsResetAt === account.pointsResetAt &&
                    next.pointsCycleStartedAt === account.pointsCycleStartedAt

                if (same) return account
                changed = true
                return next
            })

            if (!changed) return state

            localStorage.setItem(
                ACCOUNTS_STORAGE_KEY,
                JSON.stringify(nextAccounts)
            )

            return { accounts: nextAccounts }
        })
    },

    setPointStatus: (id, status) => {
        set((state) => ({
            pointStatusByAccount: {
                ...state.pointStatusByAccount,
                [id]: status,
            },
            pointErrorByAccount: {
                ...state.pointErrorByAccount,
                [id]: null,
            },
            pointLiveActiveSecondsByAccount: {
                ...state.pointLiveActiveSecondsByAccount,
                [id]: status.dailyReview.activeSeconds,
            },
        }))
    },

    setPointLiveActiveSeconds: (id, activeSeconds) => {
        set((state) => ({
            pointLiveActiveSecondsByAccount: {
                ...state.pointLiveActiveSecondsByAccount,
                [id]: activeSeconds,
            },
        }))
    },

    setPointError: (id, error) => {
        set((state) => ({
            pointErrorByAccount: {
                ...state.pointErrorByAccount,
                [id]: error,
            },
        }))
    },
    
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    
    logout: () => {
        // Clear persisted accounts on logout
        localStorage.removeItem(ACCOUNTS_STORAGE_KEY)
        localStorage.removeItem(LAST_ACTIVE_ACCOUNT_KEY)
        set({
            user: null,
            accounts: [],
            accountsLoaded: false,
            pointStatusByAccount: {},
            pointErrorByAccount: {},
            pointLiveActiveSecondsByAccount: {},
            error: null,
        })
    },
}))
